import type { LayoutOverrides } from "@profolio/templates";
import type { EditorState, EditorAction } from "./editor-types";

export const initialEditorState: EditorState = {
  draft: null,
  lastSavedDraft: null,
  acknowledgedRevision: null,
  editSequence: 0,
  savedSequence: 0,
};

export function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case "SAVE_SUCCEEDED": {
      if (!state.draft) return state;
      const { newRevision, idMappings, savedSequence } = action.payload;

      const currentOverrides = (state.draft.profile.layoutOverrides as Record<string, any>) || {};
      const currentSectionOverridesById = { ...(currentOverrides.sectionOverridesById || {}) };
      let hasRemappedOverrides = false;
      for (const [tempId, realId] of Object.entries(idMappings || {})) {
        if (currentSectionOverridesById[tempId]) {
          currentSectionOverridesById[realId] = currentSectionOverridesById[tempId];
          delete currentSectionOverridesById[tempId];
          hasRemappedOverrides = true;
        }
      }

      const updatedProfile = hasRemappedOverrides
        ? {
            ...state.draft.profile,
            layoutOverrides: {
              ...currentOverrides,
              sectionOverridesById: currentSectionOverridesById,
            },
          }
        : state.draft.profile;

      const updatedDraft = {
        ...state.draft,
        draftVersion: newRevision,
        profile: updatedProfile,
        sections: state.draft.sections.map((sec) =>
          idMappings[sec.id]
            ? { ...sec, id: idMappings[sec.id], clientKey: sec.clientKey || sec.id }
            : { ...sec, clientKey: sec.clientKey || sec.id }
        ),
      };

      return {
        ...state,
        acknowledgedRevision: newRevision,
        savedSequence,
        draft: updatedDraft,
        lastSavedDraft: updatedDraft,
      };
    }

    case "DRAFT_SAVE_CONFIRMED": {
      const { snapshot, confirmedVersion, idMappings = {} } = action.payload;
      const currentOverrides = (snapshot.profile.layoutOverrides as Record<string, any>) || {};
      const currentSectionOverridesById = { ...(currentOverrides.sectionOverridesById || {}) };
      let hasRemappedOverrides = false;
      for (const [tempId, realId] of Object.entries(idMappings || {})) {
        if (currentSectionOverridesById[tempId]) {
          currentSectionOverridesById[realId] = currentSectionOverridesById[tempId];
          delete currentSectionOverridesById[tempId];
          hasRemappedOverrides = true;
        }
      }

      const updatedProfile = hasRemappedOverrides
        ? {
            ...snapshot.profile,
            layoutOverrides: {
              ...currentOverrides,
              sectionOverridesById: currentSectionOverridesById,
            },
          }
        : snapshot.profile;

      const updatedDraft = {
        ...snapshot,
        draftVersion: confirmedVersion,
        profile: updatedProfile,
        sections: snapshot.sections.map((sec) =>
          idMappings[sec.id]
            ? { ...sec, id: idMappings[sec.id], clientKey: sec.clientKey || sec.id }
            : { ...sec, clientKey: sec.clientKey || sec.id }
        ),
      };

      return {
        ...state,
        acknowledgedRevision: confirmedVersion,
        savedSequence: state.editSequence,
        draft: updatedDraft,
        lastSavedDraft: updatedDraft,
      };
    }

    case "SET_INITIAL_DRAFT":
      return {
        ...state,
        draft: action.payload,
        lastSavedDraft: action.payload,
        acknowledgedRevision: action.payload.draftVersion,
        editSequence: state.editSequence + 1,
        savedSequence: state.editSequence + 1,
      };

    case "DISCARD_DRAFT":
      return {
        ...state,
        draft: action.payload,
        lastSavedDraft: action.payload,
        acknowledgedRevision: action.payload.draftVersion,
        editSequence: state.editSequence + 1,
        savedSequence: state.editSequence + 1,
      };

    case "UPDATE_PROFILE":
      if (!state.draft) return state;
      return {
        ...state,
        editSequence: state.editSequence + 1,
        draft: {
          ...state.draft,
          profile: {
            ...state.draft.profile,
            ...action.payload,
            layoutOverrides: action.payload.layoutOverrides !== undefined
              ? ({
                  ...((state.draft.profile.layoutOverrides as Record<string, unknown>) || {}),
                  ...((action.payload.layoutOverrides as Record<string, unknown>) || {}),
                  sections: {
                    ...(((state.draft.profile.layoutOverrides as Record<string, unknown>)?.sections as Record<string, unknown>) || {}),
                    ...(((action.payload.layoutOverrides as Record<string, unknown>)?.sections as Record<string, unknown>) || {}),
                  },
                  sectionOverridesById: {
                    ...(((state.draft.profile.layoutOverrides as Record<string, unknown>)?.sectionOverridesById as Record<string, unknown>) || {}),
                    ...(((action.payload.layoutOverrides as Record<string, unknown>)?.sectionOverridesById as Record<string, unknown>) || {}),
                  },
                  hero: {
                    ...(((state.draft.profile.layoutOverrides as Record<string, unknown>)?.hero as Record<string, unknown>) || {}),
                    ...(((action.payload.layoutOverrides as Record<string, unknown>)?.hero as Record<string, unknown>) || {}),
                  },
                  effects: {
                    ...(((state.draft.profile.layoutOverrides as Record<string, unknown>)?.effects as Record<string, unknown>) || {}),
                    ...(((action.payload.layoutOverrides as Record<string, unknown>)?.effects as Record<string, unknown>) || {}),
                  },
                } as unknown as LayoutOverrides)
              : state.draft.profile.layoutOverrides,
          },
        },
      };

    case "UPDATE_SECTION_CONTENT":
      if (!state.draft) return state;
      return {
        ...state,
        editSequence: state.editSequence + 1,
        draft: {
          ...state.draft,
          sections: state.draft.sections.map((sec) =>
            sec.id === action.payload.sectionId
              ? { ...sec, content: action.payload.content }
              : sec
          ),
        },
      };

    case "UPDATE_SECTION_VISIBILITY":
      if (!state.draft) return state;
      return {
        ...state,
        editSequence: state.editSequence + 1,
        draft: {
          ...state.draft,
          sections: state.draft.sections.map((sec) =>
            sec.id === action.payload.sectionId
              ? { ...sec, visible: action.payload.visible }
              : sec
          ),
        },
      };

    case "REORDER_SECTIONS":
      if (!state.draft) return state;
      const newSections = [...state.draft.sections];
      const [moved] = newSections.splice(action.payload.sourceIndex, 1);
      newSections.splice(action.payload.destinationIndex, 0, moved);
      // Re-assign displayOrder immutably
      const updatedSections = newSections.map((sec, idx) => ({
        ...sec,
        displayOrder: idx,
      }));
      return {
        ...state,
        editSequence: state.editSequence + 1,
        draft: {
          ...state.draft,
          sections: updatedSections,
        },
      };

    case "ADD_SECTION": {
      if (!state.draft) return state;
      const sectionToAdd = {
        ...action.payload,
        clientKey: action.payload.clientKey || crypto.randomUUID(),
      };
      return {
        ...state,
        editSequence: state.editSequence + 1,
        draft: {
          ...state.draft,
          sections: [...state.draft.sections, sectionToAdd],
        },
      };
    }

    case "DUPLICATE_SECTION": {
      if (!state.draft) return state;
      const { sourceId, newSection } = action.payload;
      const sourceIndex = state.draft.sections.findIndex((s) => s.id === sourceId);

      const updatedSections = [...state.draft.sections];
      if (sourceIndex !== -1) {
        updatedSections.splice(sourceIndex + 1, 0, newSection);
      } else {
        updatedSections.push(newSection);
      }

      return {
        ...state,
        editSequence: state.editSequence + 1,
        draft: {
          ...state.draft,
          sections: updatedSections.map((sec, idx) => ({
            ...sec,
            displayOrder: idx,
          })),
        },
      };
    }

    case "DELETE_SECTION":
      if (!state.draft) return state;
      return {
        ...state,
        editSequence: state.editSequence + 1,
        draft: {
          ...state.draft,
          sections: state.draft.sections.filter(
            (sec) => sec.id !== action.payload.sectionId
          ),
        },
      };

    case "UPDATE_DESIGN_PRESET":
      if (!state.draft) return state;
      return {
        ...state,
        editSequence: state.editSequence + 1,
        draft: {
          ...state.draft,
          designPreset: action.payload.presetId,
        },
      };

    case "APPLY_TEMPLATE_PRESET":
      if (!state.draft) return state;
      return {
        ...state,
        editSequence: state.editSequence + 1,
        draft: {
          ...state.draft,
          designPreset: action.payload.designPreset || state.draft.designPreset,
          profile: {
            ...state.draft.profile,
            ...action.payload.profile,
          },
          sections: action.payload.sections || state.draft.sections,
        },
      };

    case "REASSIGN_SECTION_PAGE":
      if (!state.draft) return state;
      return {
        ...state,
        editSequence: state.editSequence + 1,
        draft: {
          ...state.draft,
          sections: state.draft.sections.map((sec) =>
            sec.id === action.payload.sectionId
              ? { ...sec, pageId: action.payload.pageId }
              : sec
          ),
        },
      };

    default:
      return state;
  }
}
